import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Upload, Search, Flower, LogOut, Home } from 'lucide-react';
import logo from '../assets/RosaprimaLogo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #971b2f;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(212, 175, 55, 0.1);
  z-index: 1000;
  padding: 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color:rgb(255, 255, 255);
  cursor: pointer;
`;

const LogoIcon = styled(Flower)`
  width: 32px;
  height: 32px;
  color:rgb(255, 255, 255);
`;

const Navigation = styled.nav`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NavButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active ? 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#ffffff'};
  box-shadow: ${props => props.active ? '0 4px 15px rgba(212, 175, 55, 0.3)' : 'none'};

  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)' : 'rgba(212, 175, 55, 0.1)'};
    color: ${props => props.active ? 'white' : '#d4af37'};
    transform: translateY(-2px);
  }
`;

const LogoutButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  color: #ffffff;
  margin-left: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
`;

const Header = ({ activeTab, setActiveTab, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/${tab}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('podValidationCode');
    
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo onClick={() => handleTabChange('home')}>
          <img src={logo} alt="Rosaprima Logo" style={{ width: '150px', height: '120px' }} />
        </Logo>
        
        <Navigation>
          <NavButton
            active={location.pathname === '/home' || location.pathname === '/'}
            onClick={() => handleTabChange('home')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Home size={20} />
            Home
          </NavButton>

          <NavButton
            active={location.pathname === '/upload'}
            onClick={() => handleTabChange('upload')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Upload size={20} />
            Upload POD's
          </NavButton>
          
          <NavButton
            active={location.pathname === '/download'}
            onClick={() => handleTabChange('download')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Search size={20} />
            Find POD's
          </NavButton>

          <LogoutButton
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Logout and return to validation"
          >
            <LogOut size={16} />
            Logout
          </LogoutButton>
        </Navigation>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;